// Public customer-facing portal layout. Wraps every /help/:slug/* route
// with branding (theme color, hero, logo, footer) and a "My requests"
// header link when the customer is logged in.
//
// Auth state for customers is tracked separately from the agent app
// (cookie sd_customer_token). No requireAuth wrappers — anonymous users
// land directly on the homepage and can submit a ticket without logging in.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Outlet, useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Zap } from 'lucide-react';

const PortalCtx = createContext(null);
export const usePortal = () => useContext(PortalCtx);

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function PortalLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerChecked, setCustomerChecked] = useState(false);
  const [loading, setLoading] = useState(true);

  const reloadCustomer = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customer/me`, { withCredentials: true });
      setCustomer(data);
    } catch { setCustomer(null); }
    finally { setCustomerChecked(true); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/public/portal/${slug}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setData({ error: err?.response?.data?.detail || 'Portal not found' });
      } finally { if (!cancelled) setLoading(false); }
      reloadCustomer();
    })();
    return () => { cancelled = true; };
  }, [slug, reloadCustomer]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50 text-zinc-400">Loading portal…</div>;
  if (data?.error) return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="text-center">
        <p className="text-2xl font-bold text-zinc-900 mb-2">Portal unavailable</p>
        <p className="text-sm text-zinc-500">{data.error}</p>
      </div>
    </div>
  );

  const portal = data.portal;
  const theme = portal.branding?.theme_color || '#2563eb';

  const logout = async () => {
    await axios.post(`${API}/customer/logout`, {}, { withCredentials: true });
    setCustomer(null);
    navigate(`/help/${slug}`);
  };

  return (
    <PortalCtx.Provider value={{ ...data, slug, customer, customerChecked, reloadCustomer }}>
      <div className="min-h-screen flex flex-col bg-zinc-50" style={{ '--theme': theme }} data-testid="portal-layout">
        {/* Hero / Header */}
        <header style={{ background: theme }} className="text-white">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to={`/help/${slug}`} className="flex items-center gap-2.5">
              {portal.branding?.logo_url ? (
                <img src={portal.branding.logo_url} alt="logo" className="w-8 h-8 rounded bg-white object-contain p-0.5" />
              ) : (
                <div className="w-8 h-8 rounded bg-white/20 flex items-center justify-center font-bold text-sm">{portal.name.charAt(0)}</div>
              )}
              <div>
                <p className="font-semibold text-sm leading-tight">{portal.name}</p>
                <p className="text-[11px] text-white/70 leading-tight">{data.organization?.name}</p>
              </div>
            </Link>
            <nav className="flex items-center gap-3 text-sm">
              {customer ? (
                <>
                  <Link to={`/help/${slug}`} className={`px-3 py-1.5 rounded hover:bg-white/15 transition-colors ${location.pathname === `/help/${slug}` ? 'bg-white/20' : ''}`}
                    data-testid="portal-nav-new-request">New request</Link>
                  <Link to={`/help/${slug}/requests`} className={`px-3 py-1.5 rounded hover:bg-white/15 transition-colors ${location.pathname.endsWith('/requests') ? 'bg-white/20' : ''}`}
                    data-testid="portal-nav-my-requests">My requests</Link>
                  <Link to={`/help/${slug}/profile`} className={`px-3 py-1.5 rounded hover:bg-white/15 transition-colors ${location.pathname.endsWith('/profile') ? 'bg-white/20' : ''}`}
                    data-testid="portal-nav-profile">Profile</Link>
                  <button onClick={logout} className="px-3 py-1.5 rounded hover:bg-white/15" data-testid="portal-nav-logout">Log out</button>
                  <span className="text-[12px] text-white/80">{customer.name}</span>
                </>
              ) : (
                <Link to={`/help/${slug}/login`} className="px-3 py-1.5 rounded hover:bg-white/15" data-testid="portal-nav-login">Log in</Link>
              )}
            </nav>
          </div>
          {/* Intro / Hero - only on the homepage */}
          {location.pathname === `/help/${slug}` && (
            <div className="relative">
              {portal.branding?.hero_image_url && (
                <div className={`absolute inset-0 opacity-25 bg-center ${portal.branding?.hero_fit === 'contain' ? 'bg-contain bg-no-repeat' : 'bg-cover'}`} style={{ backgroundImage: `url('${portal.branding.hero_image_url}')` }} aria-hidden />
              )}
              <div className="relative max-w-5xl mx-auto px-6 pt-2 pb-12">
                <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']">How can we help?</h1>
                <p className="text-white/90 mt-2 max-w-2xl">{portal.branding?.intro_text}</p>
              </div>
            </div>
          )}
        </header>

        {/* Main content */}
        <main className="flex-1 w-full max-w-5xl mx-auto px-6 py-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="border-t border-zinc-200 bg-white mt-8" data-testid="portal-footer">
          <div className="max-w-5xl mx-auto px-6 py-6 flex flex-col items-center gap-2">
            {portal.branding?.footer_text && (
              <p className="text-xs text-zinc-500 text-center" data-testid="portal-footer-custom">{portal.branding.footer_text}</p>
            )}
            {(() => {
              const label = portal.branding?.powered_by_label || 'Inspiro Service Desk';
              const url = portal.branding?.powered_by_url || 'https://inspiroworks.com';
              const inner = (
                <>
                  <span>Powered by</span>
                  <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  <span className="font-semibold text-zinc-500">{label}</span>
                </>
              );
              return url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
                  data-testid="portal-footer-powered-by"
                >
                  {inner}
                </a>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400" data-testid="portal-footer-powered-by">
                  {inner}
                </span>
              );
            })()}
          </div>
        </footer>
      </div>
    </PortalCtx.Provider>
  );
}
